/**
 * Classification Log Analytics Component
 * Displays statistics and charts for response type classification
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getClassificationStats,
  getClassificationLogs,
  exportClassificationLogs,
  type ClassificationStats,
  type ClassificationLogRecord,
} from "@/lib/api/classificationAnalytics";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  Download,
  RefreshCw,
  Zap,
  Clock,
  BarChart3,
  TrendingUp,
  Layers,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];
const SKILL_LAYER_COLORS: Record<string, string> = {
  rule: "#00C49F",
  semantic: "#0088FE",
  llm: "#FFBB28",
  combined_llm: "#FF8042",
  none: "#8884d8",
};

export function ClassificationLogAnalytics() {
  const { messages } = useI18n();
  const t = messages.classificationAnalytics;
  const tCommon = messages.common;

  const [timeRange, setTimeRange] = useState<number>(24);
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch stats
  const {
    data: stats,
    isLoading,
    error,
    refetch,
  } = useQuery<ClassificationStats>({
    queryKey: ["classification-stats", timeRange],
    queryFn: () => getClassificationStats({ hours: timeRange }),
  });

  // Fetch recent logs
  const { data: logs } = useQuery<{
    total: number;
    records: ClassificationLogRecord[];
  }>({
    queryKey: ["classification-logs", timeRange],
    queryFn: () => getClassificationLogs({ limit: 10 }),
  });

  const handleExport = async (format: "csv" | "json") => {
    setExportLoading(true);
    try {
      await exportClassificationLogs(format, { limit: 1000 });
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export: ${error}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Prepare chart data
  const responseTypeData = stats
    ? Object.entries(stats.by_response_type).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }))
    : [];

  const methodData = stats
    ? Object.entries(stats.by_method).map(([name, value]) => ({
        name,
        count: value,
        avgLatency: stats.avg_latency_by_method[name] || 0,
      }))
    : [];

  const confidenceData = stats
    ? Object.entries(stats.avg_confidence_by_type).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        confidence: value * 100,
      }))
    : [];

  // Skill match layer distribution
  const skillLayerData = stats
    ? Object.entries(stats.by_skill_match_layer || {}).map(([name, value]) => ({
        name: name === "combined_llm" ? "Combined LLM" : name.charAt(0).toUpperCase() + name.slice(1),
        value,
        fill: SKILL_LAYER_COLORS[name] || COLORS[0],
      }))
    : [];

  // Matched skills breakdown (top 10)
  const matchedSkillsData = stats
    ? Object.entries(stats.by_matched_skill || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([name, count]) => ({
          name: name.length > 20 ? name.substring(0, 20) + "..." : name,
          fullName: name,
          count,
        }))
    : [];

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t.error}</p>
        <Button onClick={() => refetch()} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          {tCommon.retry}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={timeRange.toString()}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24">{t.timeRange.last24h}</option>
            <option value="168">{t.timeRange.last7d}</option>
            <option value="720">{t.timeRange.last30d}</option>
          </select>

          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t.refresh}
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("csv")}
            disabled={exportLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            {t.export.csv}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("json")}
            disabled={exportLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            {t.export.json}
          </Button>
        </div>
      </div>

      {/* Summary Cards - Row 1 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.totalClassifications}
            </span>
            <BarChart3 className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading ? "..." : stats?.total_classifications.toLocaleString()}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.quickResponseRate}
            </span>
            <Zap className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats
              ? Math.round(
                  ((stats.by_response_type.quick || 0) /
                    stats.total_classifications) *
                    100
                ) + "%"
              : "0%"}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.avgQueryLength}
            </span>
            <TrendingUp className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading ? "..." : Math.round(stats?.avg_query_length || 0)}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.avgLatency}
            </span>
            <Clock className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats && methodData.length > 0
              ? Math.round(
                  methodData.reduce((sum, m) => sum + m.avgLatency, 0) /
                    methodData.length
                ) + "ms"
              : "0ms"}
          </div>
        </Card>
      </div>

      {/* Summary Cards - Row 2: Skill Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.ruleMatchRate || "Rule Match Rate"}
            </span>
            <Layers className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats && stats.total_classifications > 0
              ? Math.round(
                  ((stats.by_skill_match_layer?.rule || 0) /
                    stats.total_classifications) *
                    100
                ) + "%"
              : "0%"}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {isLoading ? "" : `${stats?.by_skill_match_layer?.rule || 0} / ${stats?.total_classifications || 0}`}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.avgRuleMatchTime || "Avg Rule Match Time"}
            </span>
            <Clock className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats?.avg_rule_match_time_ms != null
              ? stats.avg_rule_match_time_ms.toFixed(1) + "ms"
              : "N/A"}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.avgLlmMatchTime || "Avg LLM Match Time"}
            </span>
            <Clock className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats?.avg_llm_match_time_ms != null
              ? stats.avg_llm_match_time_ms.toFixed(1) + "ms"
              : "N/A"}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.classification.stats.avgPromptInjection || "Avg Prompt Injection"}
            </span>
            <FileText className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats?.avg_prompt_injection_chars != null
              ? Math.round(stats.avg_prompt_injection_chars).toLocaleString() + " chars"
              : "N/A"}
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Response Type Distribution */}
        <Card className="bg-white p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t.classification.charts.responseType}
            </h3>
          </div>
          {responseTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={responseTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {responseTypeData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t.classification.noData}
            </div>
          )}
        </Card>

        {/* Confidence by Type */}
        <Card className="bg-white p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t.classification.charts.avgConfidence}
            </h3>
          </div>
          {confidenceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={confidenceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="confidence"
                  fill="#82ca9d"
                  name={`${t.skillExecution.confidence} (%)`}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t.classification.noData}
            </div>
          )}
        </Card>
      </div>

      {/* Method Performance */}
      <Card className="bg-white p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t.classification.charts.methodPerformance}
          </h3>
        </div>
        {methodData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={methodData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="count"
                fill="#8884d8"
                name={t.classification.charts.count}
              />
              <Bar
                yAxisId="right"
                dataKey="avgLatency"
                fill="#ffc658"
                name={t.classification.charts.latency}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            {t.classification.noData}
          </div>
        )}
      </Card>

      {/* Skill Metrics Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Skill Match Layer Distribution */}
        <Card className="bg-white p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t.classification.charts.skillMatchLayer || "Skill Match Layer Distribution"}
            </h3>
          </div>
          {skillLayerData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={skillLayerData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {skillLayerData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.fill || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t.classification.noData}
            </div>
          )}
        </Card>

        {/* Top Matched Skills */}
        <Card className="bg-white p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t.classification.charts.topMatchedSkills || "Top Matched Skills"}
            </h3>
          </div>
          {matchedSkillsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={matchedSkillsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={120} />
                <Tooltip
                  formatter={(value, _name, props) => [
                    value,
                    (props.payload as { fullName?: string })?.fullName || "Count",
                  ]}
                />
                <Bar dataKey="count" fill="#82ca9d" name={t.classification.charts.count} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t.classification.noData}
            </div>
          )}
        </Card>
      </div>

      {/* Recent Classifications */}
      <Card className="bg-white p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t.classification.recentClassifications}
          </h3>
        </div>
        <div className="space-y-4">
          {logs?.records.map((record) => (
            <div
              key={record.id}
              className="flex items-start justify-between border-b border-gray-200 pb-3 last:border-0"
            >
              <div className="space-y-1 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded ${
                      record.response_type === "quick"
                        ? "bg-green-100 text-green-800"
                        : record.response_type === "deep"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {record.response_type === "quick"
                      ? t.quick
                      : record.response_type === "deep"
                      ? t.deep
                      : "N/A"}
                  </span>
                  {record.response_type_confidence && (
                    <span className="text-xs text-gray-500">
                      {(record.response_type_confidence * 100).toFixed(0)}%
                    </span>
                  )}
                  {/* Skill Match Layer Badge */}
                  {record.skill_match_layer && (
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${
                        record.skill_match_layer === "rule"
                          ? "bg-emerald-100 text-emerald-700"
                          : record.skill_match_layer === "semantic"
                          ? "bg-cyan-100 text-cyan-700"
                          : record.skill_match_layer === "llm"
                          ? "bg-amber-100 text-amber-700"
                          : record.skill_match_layer === "combined_llm"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {record.skill_match_layer === "combined_llm"
                        ? "Combined LLM"
                        : record.skill_match_layer.charAt(0).toUpperCase() +
                          record.skill_match_layer.slice(1)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{record.user_query}</p>
                {/* First row: classification info */}
                <div className="flex gap-2 text-xs text-gray-500 flex-wrap">
                  {record.classification_method && (
                    <span>
                      {t.classification.method}: {record.classification_method}
                    </span>
                  )}
                  {record.response_type_latency_ms && (
                    <span>
                      • {record.response_type_latency_ms.toFixed(1)}ms
                    </span>
                  )}
                  {record.query_length && (
                    <span>
                      • {record.query_length}{" "}
                      {t.classification.stats.avgQueryLength.toLowerCase()}
                    </span>
                  )}
                </div>
                {/* Second row: skill metrics */}
                {(record.matched_skills?.length || record.total_classification_time_ms || record.prompt_injection_chars) && (
                  <div className="flex gap-2 text-xs text-gray-400 flex-wrap">
                    {record.matched_skills && record.matched_skills.length > 0 && (
                      <span title={record.matched_skills.join(", ")}>
                        Skills: {record.matched_skills.length}
                      </span>
                    )}
                    {record.skill_rule_match_time_ms != null && (
                      <span>• Rule: {record.skill_rule_match_time_ms.toFixed(1)}ms</span>
                    )}
                    {record.skill_llm_match_time_ms != null && (
                      <span>• LLM: {record.skill_llm_match_time_ms.toFixed(1)}ms</span>
                    )}
                    {record.total_classification_time_ms != null && (
                      <span>• Total: {record.total_classification_time_ms.toFixed(1)}ms</span>
                    )}
                    {record.prompt_injection_chars != null && record.prompt_injection_chars > 0 && (
                      <span>• Injected: {record.prompt_injection_chars.toLocaleString()} chars</span>
                    )}
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                {new Date(record.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
